/**
 * Floating Toolbar Component
 * 
 * A modern floating toolbar with tool selection and quick actions
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Fab,
  Badge,
  useTheme,
  alpha,
  Zoom,
} from '@mui/material';
import {
  PanTool as PanIcon,
  NearMe as SelectIcon,
  Create as DrawIcon,
  Square as RectangleIcon,
  Circle as CircleIcon,
  Timeline as LineIcon,
  TextFields as TextIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  ContentCut as CutIcon,
  FormatColorFill as FillIcon,
  BorderColor as StrokeIcon,
  Layers as LayersIcon,
  GridOn as GridIcon,
  Add as AddIcon,
  VolumeUp as SpeakerIcon,
  Mic as MicIcon,
  CameraAlt as CameraIcon,
  Router as DeviceIcon,
  TableChart as FurnitureIcon,
  MeetingRoom as DoorIcon,
} from '@mui/icons-material';

interface FloatingToolbarProps {
  selectedTool?: string;
  onToolSelect?: (tool: string) => void;
  position?: 'left' | 'right' | 'top' | 'bottom';
}

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  selectedTool = 'select',
  onToolSelect,
  position = 'left',
}) => {
  const theme = useTheme();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const tools = [
    { id: 'pan', icon: <PanIcon />, label: 'Pan (H)' },
    { id: 'select', icon: <SelectIcon />, label: 'Select (V)' },
    { id: 'draw', icon: <DrawIcon />, label: 'Draw (P)' },
    { id: 'rectangle', icon: <RectangleIcon />, label: 'Rectangle (R)' },
    { id: 'circle', icon: <CircleIcon />, label: 'Circle (C)' },
    { id: 'line', icon: <LineIcon />, label: 'Line (L)' },
    { id: 'text', icon: <TextIcon />, label: 'Text (T)' },
    { id: 'measure', icon: <MeasureIcon />, label: 'Measure (M)' },
  ];

  const deviceActions = [
    { icon: <SpeakerIcon />, name: 'Add Speaker', operation: 'add-speaker' },
    { icon: <MicIcon />, name: 'Add Microphone', operation: 'add-mic' },
    { icon: <CameraIcon />, name: 'Add Camera', operation: 'add-camera' },
    { icon: <DeviceIcon />, name: 'Add Network Device', operation: 'add-device' },
    { icon: <FurnitureIcon />, name: 'Add Furniture', operation: 'add-furniture' },
    { icon: <DoorIcon />, name: 'Add Door', operation: 'add-door' },
  ];

  const getPositionStyles = () => {
    switch (position) {
      case 'left':
        return {
          top: '50%',
          left: 16,
          transform: 'translateY(-50%)',
          flexDirection: 'column' as const,
        };
      case 'right':
        return {
          top: '50%',
          right: 16,
          transform: 'translateY(-50%)',
          flexDirection: 'column' as const,
        };
      case 'top':
        return {
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          flexDirection: 'row' as const,
        };
      case 'bottom':
        return {
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          flexDirection: 'row' as const,
        };
      default:
        return {};
    }
  };

  const positionStyles = getPositionStyles();

  return (
    <>
      {/* Main Toolbar */}
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          zIndex: theme.zIndex.fab,
          display: 'flex',
          gap: 0.5,
          p: 0.5,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          ...positionStyles,
        }}
      >
        <ToggleButtonGroup
          value={selectedTool}
          exclusive
          onChange={(_, value) => value && onToolSelect?.(value)}
          orientation={position === 'left' || position === 'right' ? 'vertical' : 'horizontal'}
          size="small"
        >
          {tools.map((tool) => (
            <ToggleButton
              key={tool.id}
              value={tool.id}
              sx={{
                border: 'none',
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
              }}
            >
              <Tooltip title={tool.label} placement={position === 'left' ? 'right' : 'left'}>
                {tool.icon}
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Divider
          orientation={position === 'left' || position === 'right' ? 'horizontal' : 'vertical'}
          flexItem
        />

        {/* Quick Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            flexDirection: positionStyles.flexDirection,
          }}
        >
          <Tooltip title="Cut (Ctrl+X)" placement={position === 'left' ? 'right' : 'left'}>
            <IconButton size="small">
              <CutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy (Ctrl+C)" placement={position === 'left' ? 'right' : 'left'}>
            <IconButton size="small">
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Paste (Ctrl+V)" placement={position === 'left' ? 'right' : 'left'}>
            <IconButton size="small">
              <PasteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete (Del)" placement={position === 'left' ? 'right' : 'left'}>
            <IconButton size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Speed Dial for Adding Elements */}
      <SpeedDial
        ariaLabel="Add elements"
        sx={{
          position: 'fixed',
          bottom: position === 'bottom' ? 80 : 16,
          right: position === 'right' ? 80 : 16,
        }}
        icon={<SpeedDialIcon icon={<AddIcon />} />}
        open={speedDialOpen}
        onOpen={() => setSpeedDialOpen(true)}
        onClose={() => setSpeedDialOpen(false)}
      >
        {deviceActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={() => {
              onToolSelect?.(action.operation);
              setSpeedDialOpen(false);
            }}
          />
        ))}
      </SpeedDial>

      {/* Context-Sensitive Floating Actions */}
      <Zoom in={selectedTool === 'draw' || selectedTool === 'rectangle'}>
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            top: 100,
            right: 16,
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box>
            <Tooltip title="Fill Color">
              <IconButton size="small">
                <FillIcon />
              </IconButton>
            </Tooltip>
            <input
              type="color"
              defaultValue="#1863D3"
              style={{
                marginLeft: 8,
                width: 32,
                height: 32,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
          </Box>
          <Box>
            <Tooltip title="Stroke Color">
              <IconButton size="small">
                <StrokeIcon />
              </IconButton>
            </Tooltip>
            <input
              type="color"
              defaultValue="#000000"
              style={{
                marginLeft: 8,
                width: 32,
                height: 32,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default FloatingToolbar;